<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import BotsAPI from 'dashboard/api/bots';
import { usePermissions } from 'dashboard/composables/usePermissions';
import CreateBotModal from './components/CreateBotModal.vue';
import ImportBotModal from './components/ImportBotModal.vue';
import { resolveTriggerLabel } from './components/triggerLabels';
import { TRIGGER_GROUPS, buildTriggerPayload } from './components/triggerCatalog';

const { isAdminOrSuperAdmin } = usePermissions();

// Devuelve los disparadores resueltos (label + icon) para mostrar en la celda.
// Acepta tanto el array nuevo (triggers_compact) como el legacy trigger_summary.
function resolvedTriggers(bot) {
  const arr = Array.isArray(bot?.triggers_compact) ? bot.triggers_compact : [];
  return arr.map(t => ({ ...resolveTriggerLabel(t), id: t.id }));
}

// Estado del popover "+N más" (qué bot tiene el popover abierto).
const triggerPopoverBotId = ref(null);
function toggleTriggerPopover(botId, ev) {
  ev?.stopPropagation();
  triggerPopoverBotId.value = triggerPopoverBotId.value === botId ? null : botId;
}
function closeTriggerPopover() { triggerPopoverBotId.value = null; }

// ── Selector inline "+ Disparador" (estilo Kommo · hover-reveal) ──
const addTriggerBotId = ref(null);
const addTriggerSearch = ref('');
const addingTrigger = ref(false);

function openAddTrigger(botId, ev) {
  ev?.stopPropagation();
  closeTriggerPopover();
  addTriggerSearch.value = '';
  addTriggerBotId.value = addTriggerBotId.value === botId ? null : botId;
}
function closeAddTrigger() { addTriggerBotId.value = null; }

const filteredTriggerGroups = computed(() => {
  const q = addTriggerSearch.value.trim().toLowerCase();
  if (!q) return TRIGGER_GROUPS;
  return TRIGGER_GROUPS
    .map(g => ({ ...g, options: g.options.filter(o => o.label.toLowerCase().includes(q)) }))
    .filter(g => g.options.length);
});

async function addTriggerToBot(bot, option, ev) {
  ev?.stopPropagation();
  if (option.soon || addingTrigger.value) return;
  addingTrigger.value = true;
  try {
    await BotsAPI.createTrigger(bot.id, buildTriggerPayload(option));
    closeAddTrigger();
    await loadBots();
  } finally {
    addingTrigger.value = false;
  }
}

// ── Eliminar disparador desde la lista (Variante C · solo super_admin) ──
// `${botId}:${index}` que está en confirmación pendiente (1er click en ×).
const pendingDeleteKey = ref(null);
const deletingTrigger = ref(false);
// Chip resaltado en rojo/tachado mientras el mouse está sobre su × (Variante C).
const hoverDelKey = ref(null);

function delKey(bot, idx) { return `${bot.id}:${idx}`; }

function requestDeleteTrigger(bot, idx, ev) {
  ev?.stopPropagation();
  pendingDeleteKey.value = delKey(bot, idx);
}
function cancelDeleteTrigger(ev) {
  ev?.stopPropagation();
  pendingDeleteKey.value = null;
}
async function confirmDeleteTrigger(bot, trigger, ev) {
  ev?.stopPropagation();
  if (!trigger?.id || deletingTrigger.value) return;
  deletingTrigger.value = true;
  try {
    await BotsAPI.deleteTrigger(bot.id, trigger.id);
    pendingDeleteKey.value = null;
    await loadBots();
  } finally {
    deletingTrigger.value = false;
  }
}

// ── Ordenamiento de la lista (persistido en localStorage) ──
const SORT_OPTIONS = [
  { key: 'newest',       label: 'Más nuevos primero',     icon: '📅' },
  { key: 'oldest',       label: 'Más antiguos primero',   icon: '📅' },
  { key: 'launches',     label: 'Más lanzamientos',       icon: '🚀' },
  { key: 'conversion',   label: 'Mejor conversión',       icon: '🎯' },
  { key: 'name_asc',     label: 'Alfabético A-Z',         icon: '🔠' },
  { key: 'name_desc',    label: 'Alfabético Z-A',         icon: '🔠' },
];
const SORT_STORAGE_KEY = 'victorpos:salesbots-sort';
const sortBy = ref(localStorage.getItem(SORT_STORAGE_KEY) || 'newest');
const sortMenuOpen = ref(false);
function toggleSortMenu(ev) { ev?.stopPropagation(); sortMenuOpen.value = !sortMenuOpen.value; }
function closeSortMenu() { sortMenuOpen.value = false; }
function setSort(key) {
  sortBy.value = key;
  try { localStorage.setItem(SORT_STORAGE_KEY, key); } catch (e) { /* ignore */ }
  sortMenuOpen.value = false;
}

const currentSortLabel = computed(() => {
  const opt = SORT_OPTIONS.find(o => o.key === sortBy.value);
  return opt ? opt.label : 'Más nuevos primero';
});

// Buscador inteligente · debe declararse ANTES de filteredBots / highlightedName.
const searchQuery = ref('');
const searchInputRef = ref(null);
function focusSearch(ev) {
  // Cmd+K (mac) o Ctrl+K (win/linux) enfoca el buscador.
  if ((ev.metaKey || ev.ctrlKey) && (ev.key === 'k' || ev.key === 'K')) {
    ev.preventDefault();
    searchInputRef.value?.focus?.();
  }
}
function normalizeText(s) {
  return (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

// Favoritos (G3 · M1.B) · persiste en localStorage. Máximo 8 bots marcados.
const MAX_FAVORITES = 8;
const FAVORITES_STORAGE_KEY = 'victorpos:salesbots-favorites';
const favoriteIds = ref(new Set());
function loadFavorites() {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) favoriteIds.value = new Set(arr.map(Number).filter(Number.isFinite));
  } catch (e) { /* localStorage no disponible o JSON corrupto · ignoramos */ }
}
function saveFavorites() {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify([...favoriteIds.value]));
  } catch (e) { /* ignore */ }
}
function isFavorite(bot) { return favoriteIds.value.has(bot.id); }
const favoritesCount = computed(() => favoriteIds.value.size);
const canAddMoreFavorites = computed(() => favoritesCount.value < MAX_FAVORITES);
function toggleFavorite(bot, ev) {
  ev?.stopPropagation();
  const next = new Set(favoriteIds.value);
  if (next.has(bot.id)) {
    next.delete(bot.id);
  } else {
    if (next.size >= MAX_FAVORITES) return; // límite alcanzado · click sin efecto
    next.add(bot.id);
  }
  favoriteIds.value = next;
  saveFavorites();
}
loadFavorites();

const sortedBots = computed(() => {
  const list = [...bots.value];
  switch (sortBy.value) {
    case 'oldest':     list.sort((a, b) => (a.created_at || 0) - (b.created_at || 0)); break;
    case 'launches':   list.sort((a, b) => (b.launches_total || 0) - (a.launches_total || 0)); break;
    case 'conversion': list.sort((a, b) => (b.conversion_rate || 0) - (a.conversion_rate || 0)); break;
    case 'name_asc':   list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')); break;
    case 'name_desc':  list.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'es')); break;
    default:           list.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
  }
  // Favoritos SIEMPRE al tope, sin importar el criterio de orden. Array.sort
  // es estable → dentro de cada grupo se mantiene el orden del criterio elegido.
  return list.sort((a, b) =>
    (favoriteIds.value.has(b.id) ? 1 : 0) - (favoriteIds.value.has(a.id) ? 1 : 0)
  );
});

// Lista final: orden (favoritos arriba) + buscador por nombre normalizado.
const filteredBots = computed(() => {
  const q = normalizeText(searchQuery.value.trim());
  if (!q) return sortedBots.value;
  return sortedBots.value.filter(b => normalizeText(b.name).includes(q));
});

// Devuelve el nombre del bot con los matches envueltos en <mark> dorado.
// Escapa HTML para evitar XSS antes de aplicar el highlight.
function escapeHtml(s) {
  return (s || '').toString()
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function highlightedName(name) {
  const q = searchQuery.value.trim();
  if (!q) return escapeHtml(name);
  const safeName = escapeHtml(name);
  const nq = normalizeText(q);
  const nName = normalizeText(name);
  let out = '';
  let i = 0;
  while (i < nName.length) {
    const idx = nName.indexOf(nq, i);
    if (idx === -1) { out += safeName.slice(i); break; }
    out += safeName.slice(i, idx);
    out += '<mark class="sb-search-mark">' + safeName.slice(idx, idx + nq.length) + '</mark>';
    i = idx + nq.length;
  }
  return out;
}

const router = useRouter();
const route = useRoute();

const bots = ref([]);
const loading = ref(true);
const error = ref(null);
const showCreate = ref(false);
const showImport = ref(false);


// Multi-select
const selectedIds = ref(new Set());
const someSelected = computed(() => selectedIds.value.size > 0);
const allSelected = computed(
  () => bots.value.length > 0 && selectedIds.value.size === bots.value.length
);
function isSelected(bot) {
  return selectedIds.value.has(bot.id);
}
function toggleSelect(bot, ev) {
  ev.stopPropagation();
  const next = new Set(selectedIds.value);
  if (next.has(bot.id)) next.delete(bot.id);
  else next.add(bot.id);
  selectedIds.value = next;
}
function toggleSelectAll() {
  if (allSelected.value) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(bots.value.map(b => b.id));
  }
}
function clearSelection() {
  selectedIds.value = new Set();
}
function selectedBots() {
  return bots.value.filter(b => selectedIds.value.has(b.id));
}

async function bulkPause() {
  const list = selectedBots().filter(b => b.active);
  for (const bot of list) {
    try {
      const { data } = await BotsAPI.toggleActive(bot.id);
      Object.assign(bot, data);
    } catch (e) { /* silent — sigue con el siguiente */ }
  }
  clearSelection();
}
async function bulkActivate() {
  const list = selectedBots().filter(b => !b.active);
  for (const bot of list) {
    try {
      const { data } = await BotsAPI.toggleActive(bot.id);
      Object.assign(bot, data);
    } catch (e) { /* silent */ }
  }
  clearSelection();
}
async function bulkDuplicate() {
  const list = selectedBots();
  for (const bot of list) {
    try {
      const { data } = await BotsAPI.duplicate(bot.id);
      bots.value.unshift(data);
    } catch (e) { /* silent */ }
  }
  clearSelection();
}
async function bulkDelete() {
  const list = selectedBots();
  if (!list.length) return;
  for (const bot of list) {
    try {
      await BotsAPI.delete(bot.id);
      bots.value = bots.value.filter(b => b.id !== bot.id);
    } catch (e) { /* silent */ }
  }
  clearSelection();
}

async function loadBots() {
  loading.value = true;
  try {
    const { data } = await BotsAPI.get();
    bots.value = data.payload || [];
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
  } finally {
    loading.value = false;
  }
}

function formatNumber(n) {
  if (n == null) return '0';
  return n.toLocaleString('es-MX');
}

function rateClass(rate) {
  if (rate >= 80) return 'text-emerald-600';
  if (rate >= 30) return 'text-amber-600';
  return 'text-n-slate-11';
}

function openCreate() {
  showCreate.value = true;
}

function openImport() {
  showImport.value = true;
}

function onBotImported() {
  showImport.value = false;
  loadBots();
}

function onBotCreated(bot) {
  showCreate.value = false;
  bots.value.unshift(bot);
  router.push({ name: 'salesbots_editor', params: { ...route.params, botId: bot.id } });
}

function openBot(bot) {
  router.push({ name: 'salesbots_editor', params: { ...route.params, botId: bot.id } });
}

async function toggleActive(bot, ev) {
  ev.stopPropagation();
  const { data } = await BotsAPI.toggleActive(bot.id);
  Object.assign(bot, data);
}

async function duplicateBot(bot, ev) {
  ev.stopPropagation();
  const { data } = await BotsAPI.duplicate(bot.id);
  bots.value.unshift(data);
}

async function destroyBot(bot, ev) {
  ev.stopPropagation();
  await BotsAPI.delete(bot.id);
  bots.value = bots.value.filter(b => b.id !== bot.id);
}

// Cierra el popover de "+N más" al hacer click fuera (en cualquier parte de la página).
function onDocumentClick() { closeTriggerPopover(); closeAddTrigger(); pendingDeleteKey.value = null; }

// Re-cargar la lista cada vez que el usuario regresa a la ruta de salesbots
// (ej. después de configurar un disparador en el editor). Sin esto, los
// triggers_compact y demás stats quedan obsoletos hasta refrescar la página.
// Reusamos el `route` ya declarado al inicio del setup.
watch(() => route.path, (newPath, oldPath) => {
  if (newPath !== oldPath && newPath.match(/\/salesbots\/?$/)) {
    loadBots();
  }
});

onMounted(() => {
  loadBots();
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', focusSearch);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', focusSearch);
});
</script>

<template>
  <div class="w-full bg-n-background">
    <!-- Header full-width -->
    <div class="px-10 py-6">
      <div class="flex items-end justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">Salesbots</h1>
          <p class="text-sm text-n-slate-11 mt-1.5">
            Construye bots sin código para automatizar chats y tareas. Comienza desde cero o usa una plantilla,
            personalízala y añade disparadores.
          </p>
        </div>
      </div>

      <div v-if="loading" class="text-sm text-n-slate-11 py-12 text-center">Cargando bots…</div>
      <div v-else-if="error" class="text-sm text-red-500 py-12 text-center">{{ error }}</div>

      <!-- Toolbar: contador + buscador inteligente prominente + sort + CTA inline (G3 · M1.B) -->
      <div v-else class="flex items-center gap-3 mt-4 px-1">
        <span class="text-[11px] text-n-slate-11 whitespace-nowrap">
          <b class="text-amber-500" v-if="searchQuery.trim()">{{ filteredBots.length }}</b><span v-if="searchQuery.trim()"> de </span>{{ bots.length }}
          {{ bots.length === 1 ? 'bot' : 'bots' }}
          <span v-if="favoritesCount" class="text-n-slate-10">· ⭐ {{ favoritesCount }} arriba</span>
        </span>

        <!-- BUSCADOR INTELIGENTE -->
        <div
          class="flex-1 flex items-center gap-2 px-3 py-2 bg-n-background border rounded-lg transition"
          :class="searchQuery ? 'border-amber-500/55 shadow-[0_0_0_3px_rgba(245,158,11,0.10)]' : 'border-n-weak hover:border-n-slate-6'"
        >
          <span class="text-amber-500 text-sm leading-none">🔍</span>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            placeholder="Buscar bot por nombre..."
            class="flex-1 bg-transparent border-0 outline-none text-[13px] text-n-slate-12 placeholder:text-n-slate-9"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="px-1.5 py-0.5 rounded text-[11px] text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-black1 transition"
            title="Limpiar"
            @click="searchQuery = ''"
          >×</button>
          <span class="hidden sm:inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-n-alpha-black1 text-n-slate-11">⌘K</span>
        </div>

        <span class="text-n-slate-11 text-[11px]">·</span>
        <div v-on-click-outside="closeSortMenu" class="relative">
          <button type="button"
                  class="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-n-weak bg-n-background hover:bg-n-alpha-black1 transition"
                  :class="sortMenuOpen ? 'border-amber-500/55 text-amber-600 dark:text-amber-300' : ''"
                  @click="toggleSortMenu">
            <span class="text-n-slate-11">Ordenar:</span>
            <span class="font-medium text-n-slate-12">{{ currentSortLabel }}</span>
            <span class="text-[8px] text-n-slate-11">▼</span>
          </button>
          <div v-if="sortMenuOpen"
               class="absolute z-30 right-0 top-full mt-1 w-56 rounded-lg shadow-2xl border border-n-weak bg-n-background py-1"
               @click.stop>
            <div class="text-[9px] uppercase tracking-wide font-semibold text-n-slate-11 px-3 py-1.5">Ordenar por</div>
            <button v-for="opt in SORT_OPTIONS" :key="opt.key"
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-1.5 text-[12px] text-n-slate-12 hover:bg-n-alpha-black1 text-left"
                    :class="sortBy === opt.key ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : ''"
                    @click="setSort(opt.key)">
              <span>{{ opt.icon }} {{ opt.label }}</span>
              <span v-if="sortBy === opt.key" class="text-emerald-500 text-[11px]">✓</span>
            </button>
          </div>
        </div>

        <!-- CTA inline: "+ Crear o importar bot" (G3 · M1.B) · solo super_admin -->
        <button
          v-if="isAdminOrSuperAdmin"
          type="button"
          class="px-4 py-2 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 shadow-md shadow-amber-500/30 hover:brightness-110 transition whitespace-nowrap"
          @click="openCreate"
        >
          <span class="text-base leading-none font-black">+</span> Crear o importar bot
        </button>
      </div>

      <div v-if="!loading && !error" class="bg-n-background border border-n-weak rounded-xl mt-2 overflow-y-auto" style="max-height: calc(100vh - 280px)">
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-black1 text-[11px] uppercase tracking-wider text-n-slate-11 sticky top-0 z-10">
            <tr>
              <th class="px-3 py-3 w-10 text-center" title="Marcar como favorito">★</th>
              <th class="px-4 py-3 w-8">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate.prop="someSelected && !allSelected"
                  class="w-3.5 h-3.5 rounded border-n-weak cursor-pointer"
                  @click.stop="toggleSelectAll"
                />
              </th>
              <th class="text-left px-5 py-3 font-medium w-[36%]">Nombre</th>
              <th class="text-left px-5 py-3 font-medium">Disparadores</th>
              <th class="text-right px-5 py-3 font-medium">Tasa de conversión</th>
              <th class="text-right px-5 py-3 font-medium">Lanzamientos totales</th>
              <th class="text-right px-5 py-3 font-medium">Sesiones activas</th>
              <th class="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <!-- Fila de acciones masivas (visible si hay selección) -->
            <tr v-if="someSelected" class="bg-blue-500/10 border-b-2 border-blue-500/40">
              <td colspan="8" class="px-4 py-2 text-[12px]">
                <div class="flex items-center gap-3 flex-wrap">
                  <span class="text-blue-600 dark:text-blue-300"><b>{{ selectedIds.size }}</b> seleccionado(s) de {{ bots.length }}</span>
                  <span class="text-n-slate-9">·</span>
                  <button class="text-amber-600 dark:text-amber-300 hover:underline inline-flex items-center gap-1" @click="bulkPause">
                    <span class="i-lucide-pause w-3 h-3" /> Pausar
                  </button>
                  <span class="text-n-slate-9">·</span>
                  <button class="text-emerald-600 dark:text-emerald-300 hover:underline inline-flex items-center gap-1" @click="bulkActivate">
                    <span class="i-lucide-play w-3 h-3" /> Activar
                  </button>
                  <span class="text-n-slate-9">·</span>
                  <button class="text-n-slate-12 hover:underline inline-flex items-center gap-1" @click="bulkDuplicate">
                    <span class="i-lucide-copy w-3 h-3" /> Duplicar
                  </button>
                  <span class="text-n-slate-9">·</span>
                  <button v-if="isAdminOrSuperAdmin" class="text-rose-600 dark:text-rose-300 hover:underline inline-flex items-center gap-1" @click="bulkDelete">
                    <span class="i-lucide-trash-2 w-3 h-3" /> Eliminar
                  </button>
                  <button class="ml-auto text-n-slate-11 hover:text-n-slate-12 text-xs" @click="clearSelection">deseleccionar todo</button>
                </div>
              </td>
            </tr>

            <tr v-if="!bots.length">
              <td colspan="8" class="px-5 py-10 text-center text-n-slate-11 text-sm">
                Aún no hay bots. Pulsa "+ Crear o importar bot" en la barra superior.
              </td>
            </tr>

            <tr v-else-if="!filteredBots.length && searchQuery.trim()">
              <td colspan="8" class="px-5 py-10 text-center text-n-slate-11 text-sm">
                Sin resultados para "<b class="text-amber-500">{{ searchQuery }}</b>".
                <button class="ml-2 text-blue-500 hover:underline" @click="searchQuery = ''">Limpiar búsqueda</button>
              </td>
            </tr>

            <tr
              v-for="bot in filteredBots"
              :key="bot.id"
              class="hover:bg-n-alpha-black1 cursor-pointer group"
              :class="isSelected(bot) ? 'bg-blue-500/10' : ''"
              @click="openBot(bot)"
            >
              <!-- Estrella favorita (G3 · M1.B) -->
              <td class="px-3 py-3.5 text-center" @click.stop>
                <button
                  type="button"
                  class="text-[15px] leading-none transition"
                  :class="isFavorite(bot)
                    ? 'text-amber-500 drop-shadow-[0_0_3px_rgba(251,191,36,0.5)]'
                    : (canAddMoreFavorites ? 'text-n-slate-9 hover:text-amber-500/70' : 'text-n-slate-9 opacity-40 cursor-not-allowed')"
                  :title="isFavorite(bot)
                    ? 'Quitar de favoritos'
                    : (canAddMoreFavorites ? 'Marcar como favorito' : 'Máximo 8 favoritos · quita uno primero')"
                  :disabled="!isFavorite(bot) && !canAddMoreFavorites"
                  @click="toggleFavorite(bot, $event)"
                >{{ isFavorite(bot) ? '★' : '☆' }}</button>
              </td>
              <td class="px-4 py-3.5" @click.stop>
                <input
                  type="checkbox"
                  :checked="isSelected(bot)"
                  class="w-3.5 h-3.5 rounded border-n-weak cursor-pointer"
                  @click="toggleSelect(bot, $event)"
                />
              </td>
              <td class="px-5 py-3.5">
                <div class="flex items-center gap-2">
                  <!-- v-html seguro: highlightedName escapa HTML antes de envolver matches en <mark> -->
                  <span class="text-blue-600 font-medium" v-html="highlightedName(bot.name)"></span>
                  <span v-if="!bot.active" class="text-[10px] uppercase px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-11">pausado</span>
                </div>
              </td>
              <td class="px-5 py-3.5 relative">
                <template v-if="resolvedTriggers(bot).length">
                  <span class="inline-flex items-center gap-1.5 align-middle">
                    <span
                      v-for="(t, i) in resolvedTriggers(bot).slice(0, 2)"
                      :key="i"
                      class="group/chip inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-md border transition-colors"
                      :class="hoverDelKey === delKey(bot, i)
                        ? 'bg-red-500/10 border-red-500/40 text-red-400 line-through'
                        : 'border-transparent text-n-slate-12'"
                    >
                      <span>{{ t.icon }}</span><span>{{ t.label }}</span>

                      <!-- Confirmación inline (regla destructivo · solo super admin) -->
                      <template v-if="isAdminOrSuperAdmin">
                        <template v-if="pendingDeleteKey === delKey(bot, i)">
                          <button
                            type="button"
                            class="ml-1 w-4 h-4 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] leading-none"
                            title="Confirmar eliminar"
                            :disabled="deletingTrigger"
                            @click="confirmDeleteTrigger(bot, t, $event)"
                          >✓</button>
                          <button
                            type="button"
                            class="w-4 h-4 inline-flex items-center justify-center rounded-full bg-n-slate-3 text-n-slate-11 text-[10px] leading-none"
                            title="Cancelar"
                            @click="cancelDeleteTrigger($event)"
                          >×</button>
                        </template>
                        <button
                          v-else
                          type="button"
                          class="ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full bg-red-500/15 text-red-400 text-[11px] leading-none opacity-0 group-hover/chip:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
                          title="Eliminar disparador"
                          @mouseenter="hoverDelKey = delKey(bot, i)"
                          @mouseleave="hoverDelKey = null"
                          @click="requestDeleteTrigger(bot, i, $event)"
                        >×</button>
                      </template>
                    </span>
                  </span>
                  <button
                    v-if="resolvedTriggers(bot).length > 2"
                    type="button"
                    class="ml-2 inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200"
                    :title="`Ver todos los ${resolvedTriggers(bot).length} disparadores`"
                    @click="toggleTriggerPopover(bot.id, $event)"
                  >
                    +{{ resolvedTriggers(bot).length - 2 }} más
                  </button>
                  <!-- Popover con todos los disparadores -->
                  <div
                    v-if="triggerPopoverBotId === bot.id"
                    class="absolute z-50 mt-2 left-5 bg-n-background border border-n-weak rounded-lg shadow-2xl py-1.5 min-w-[280px]"
                    @click.stop
                  >
                    <div class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11 px-3 py-1.5 border-b border-n-weak">
                      Todos los disparadores ({{ resolvedTriggers(bot).length }})
                    </div>
                    <div
                      v-for="(t, i) in resolvedTriggers(bot)"
                      :key="`pop-${i}`"
                      class="flex items-center gap-2 px-3 py-1.5 text-[12px] text-n-slate-12 hover:bg-n-alpha-black1"
                    >
                      <span>{{ t.icon }}</span><span>{{ t.label }}</span>
                    </div>
                    <button
                      type="button"
                      class="text-[10px] text-n-slate-11 hover:text-n-slate-12 px-3 py-1 mt-0.5 w-full text-right"
                      @click="closeTriggerPopover"
                    >cerrar</button>
                  </div>
                </template>
                <span v-else class="text-[11px] text-n-slate-11 italic">Sin disparador configurado</span>

                <!-- Botón "+ Disparador" hover-reveal (Variante C · estilo Kommo) -->
                <button
                  type="button"
                  class="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md border border-dashed border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6 hover:bg-n-alpha-black1 transition-opacity opacity-0 group-hover:opacity-100"
                  :class="addTriggerBotId === bot.id ? 'opacity-100' : ''"
                  :title="'Agregar disparador'"
                  @click="openAddTrigger(bot.id, $event)"
                >
                  <span class="text-[13px] leading-none">+</span>
                  <span v-if="!resolvedTriggers(bot).length">Disparador</span>
                </button>

                <!-- Popover selector de disparador -->
                <div
                  v-if="addTriggerBotId === bot.id"
                  class="absolute z-50 mt-2 left-5 w-[340px] bg-n-background border border-n-weak rounded-xl shadow-2xl overflow-hidden"
                  @click.stop
                >
                  <div class="p-2.5 border-b border-n-weak">
                    <input
                      v-model="addTriggerSearch"
                      type="text"
                      placeholder="Buscar disparador…"
                      class="w-full px-3 py-2 rounded-lg bg-n-alpha-black1 border border-n-weak text-[12px] text-n-slate-12 focus:outline-none focus:border-n-slate-6"
                    />
                  </div>
                  <div class="max-h-[300px] overflow-y-auto py-1">
                    <template v-for="g in filteredTriggerGroups" :key="g.key">
                      <div class="text-[9px] font-bold uppercase tracking-wide text-n-slate-10 px-3 pt-2.5 pb-1">
                        {{ g.icon }} {{ g.label }}
                      </div>
                      <button
                        v-for="opt in g.options"
                        :key="opt.key"
                        type="button"
                        class="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-left transition"
                        :class="opt.soon
                          ? 'text-n-slate-10 cursor-not-allowed'
                          : 'text-n-slate-12 hover:bg-n-alpha-black1 cursor-pointer'"
                        :disabled="opt.soon || addingTrigger"
                        @click="addTriggerToBot(bot, opt, $event)"
                      >
                        <span class="w-5 text-center">{{ opt.icon }}</span>
                        <span class="flex-1">{{ opt.label }}</span>
                        <span v-if="opt.soon" class="text-[9px] uppercase px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-10">pronto</span>
                      </button>
                    </template>
                    <div v-if="!filteredTriggerGroups.length" class="px-3 py-4 text-[12px] text-n-slate-11 text-center">
                      Sin resultados
                    </div>
                  </div>
                  <div class="flex items-center justify-between px-3 py-2 border-t border-n-weak text-[10px] text-n-slate-11">
                    <span>La configuración fina se ajusta en el editor</span>
                    <button type="button" class="hover:text-n-slate-12" @click="closeAddTrigger">cerrar</button>
                  </div>
                </div>
              </td>
              <td class="px-5 py-3.5 text-right" :class="rateClass(bot.conversion_rate)">
                {{ bot.conversion_rate }}%
              </td>
              <td class="px-5 py-3.5 text-right text-n-slate-12">{{ formatNumber(bot.launches_total) }}</td>
              <td class="px-5 py-3.5 text-right" :class="bot.active_sessions > 0 ? 'text-blue-600 font-semibold' : 'text-n-slate-11'">
                {{ formatNumber(bot.active_sessions) }}
              </td>
              <td class="px-3 py-3.5 text-right">
                <div class="invisible group-hover:visible inline-flex items-center gap-1">
                  <button class="p-1.5 rounded hover:bg-n-slate-3" :title="bot.active ? 'Pausar' : 'Activar'" @click="toggleActive(bot, $event)">
                    <span :class="bot.active ? 'i-lucide-pause' : 'i-lucide-play'" class="w-3.5 h-3.5" />
                  </button>
                  <button class="p-1.5 rounded hover:bg-n-slate-3" title="Duplicar" @click="duplicateBot(bot, $event)">
                    <span class="i-lucide-copy w-3.5 h-3.5" />
                  </button>
                  <button v-if="isAdminOrSuperAdmin" class="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar" @click="destroyBot(bot, $event)">
                    <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="bots.length" class="px-4 py-2 text-center border-t border-n-weak">
          <span class="text-xs text-n-slate-11">{{ bots.length }} bot(s)</span>
        </div>
      </div>
    </div>

    <CreateBotModal v-if="showCreate" @close="showCreate = false" @created="onBotCreated" />
    <ImportBotModal v-if="showImport" @close="showImport = false" @imported="onBotImported" />
  </div>
</template>

<style scoped>
/* Highlight de matches del buscador inteligente en los nombres de bots */
:deep(.sb-search-mark) {
  background: rgba(251, 191, 36, 0.30);
  color: #fbbf24;
  padding: 1px 3px;
  border-radius: 3px;
  font-weight: 700;
}
</style>
